import { FC } from "react";

import styled from "@emotion/styled";
import { motion } from "framer-motion";
import Translate from "src/ui/components/Translate/Translate";

import { formatBytes } from "@9amhealth/shared";

const Overlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  max-width: 300px;
  width: 100%;
`;

const ProgressText = styled.div`
  text-align: center;
  color: white;
  transition: opacity 0.3s ease-in-out;
`;

const ProgressTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0 0 0.5rem 0;
  color: white;
`;

const ProgressSubtitle = styled.p`
  font-size: 0.875rem;
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled.div<{
  percentage: number;
}>`
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(83, 56, 158, 1) 0%,
    rgba(105, 65, 198, 1) 100%
  );
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${(props) => props.percentage}%;

  &[data-analyzing="true"] {
    animation: shimmer 2s infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: 200px 0;
    }
  }
`;

const ProgressLabel = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 0.5rem;
  text-align: center;
  transition: opacity 0.3s ease-in-out;
`;

interface MealInsightProgressProps {
  phase: "uploading" | "analyzing";
  percentage: number;
  loaded: number;
  total: number;
}

const MealInsightProgress: FC<MealInsightProgressProps> = ({
  phase,
  percentage,
  loaded,
  total
}) => {
  const isUploading = phase === "uploading";
  const isAnalyzing = phase === "analyzing";

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      <ProgressContainer>
        <ProgressText>
          <ProgressTitle>
            {isUploading && <Translate msg="upload.uploading" />}
            {isAnalyzing && <Translate msg="mealInsights.analyzing" />}
          </ProgressTitle>
          <ProgressSubtitle>
            {isUploading && <Translate msg="upload.pleaseWait" />}
          </ProgressSubtitle>
        </ProgressText>

        <div style={{ width: "100%" }}>
          <ProgressBarContainer>
            <ProgressBarFill
              percentage={percentage}
              data-analyzing={isAnalyzing}
            />
          </ProgressBarContainer>

          <ProgressLabel>
            {isUploading && total > 0 && (
              <>{`${formatBytes(loaded)} / ${formatBytes(total)} • ${Math.round(percentage)}%`}</>
            )}
            {isAnalyzing && <>{`${Math.round(percentage)}%`}</>}
          </ProgressLabel>
        </div>
      </ProgressContainer>
    </Overlay>
  );
};

export default MealInsightProgress;
